/**
 * geminiClient.ts
 * Typed wrapper for the Google Gemini API (gemini-2.5-flash).
 * Used exclusively by the AI Quiz Builder feature.
 */

import { GoogleGenerativeAI, type GenerativeModel } from '@google/generative-ai';
import type { AIGeneratedQuestion, VideoQuestion, MCOption, BloomsLevel } from '@types-local/quiz.types';
import type { SlideTextContent } from '@types-local/office.types';
import { buildSystemPrompt, buildUserPrompt } from './quizPrompt';

// ---- Client initialization ---------------------------------

let _genAI: GoogleGenerativeAI | null = null;
let _model: GenerativeModel | null = null;

function getModel(): GenerativeModel {
  if (_model) return _model;
  const apiKey = import.meta.env.VITE_GEMINI_API_KEY;
  if (!apiKey) throw new Error('VITE_GEMINI_API_KEY is not set in .env');

  _genAI = new GoogleGenerativeAI(apiKey);
  _model = _genAI.getGenerativeModel({
    model: import.meta.env.VITE_GEMINI_MODEL ?? 'gemini-2.5-flash',
    systemInstruction: buildSystemPrompt(),
    generationConfig: {
      // Force deterministic, parseable JSON output
      temperature: 0.4,
      topK: 40,
      topP: 0.95,
      maxOutputTokens: 1024,
      responseMimeType: 'application/json',
    },
  });

  return _model;
}

// ---- Public API --------------------------------------------

/**
 * Generates a single quiz question from slide text content.
 *
 * @param slideContent - Text extracted from the current slide
 * @param targetBlooms - Optional Bloom's level constraint
 * @returns Typed AIGeneratedQuestion or throws on parse/API failure
 */
export async function generateQuizQuestion(
  slideContent: SlideTextContent,
  targetBlooms: BloomsLevel | null = null,
): Promise<AIGeneratedQuestion> {
  if (!slideContent.allText.trim()) {
    throw new Error('No slide content found. Add text to the slide before generating questions.');
  }

  const model = getModel();
  const userPrompt = buildUserPrompt({
    slideTitle: slideContent.title,
    slideBody: slideContent.bodyLines.join('\n'),
    targetBlooms,
    questionCount: 1,
  });

  let rawText = '';
  try {
    const result = await model.generateContent(userPrompt);
    rawText = result.response.text().trim();
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    throw new Error(`Gemini API error: ${message}`);
  }

  // Parse and validate the response
  return parseAndValidateQuestion(rawText);
}

/**
 * Validates and parses the JSON response from Gemini.
 * Throws a descriptive error if the schema doesn't match.
 */
function parseAndValidateQuestion(rawText: string): AIGeneratedQuestion {
  let parsed: Partial<AIGeneratedQuestion>;

  try {
    // Strip any accidental markdown fences
    const cleaned = rawText.replace(/^```json?\n?|```$/g, '').trim();
    parsed = JSON.parse(cleaned);
  } catch {
    throw new Error(`AI returned invalid JSON. Raw response: ${rawText.slice(0, 200)}`);
  }

  const { question, options, correctIndex, blooms, explanation } = parsed;

  if (typeof question !== 'string' || !question.trim()) {
    throw new Error('AI response missing "question" field.');
  }
  if (!Array.isArray(options) || options.length !== 4 || !options.every((o) => typeof o === 'string')) {
    throw new Error('AI response "options" must be an array of exactly 4 strings.');
  }
  if (typeof correctIndex !== 'number' || correctIndex < 0 || correctIndex > 3) {
    throw new Error('AI response "correctIndex" must be 0, 1, 2, or 3.');
  }
  const validBlooms: BloomsLevel[] = ['Remember', 'Understand', 'Apply', 'Analyze', 'Evaluate', 'Create'];
  if (!blooms || !validBlooms.includes(blooms as BloomsLevel)) {
    throw new Error(`AI response "blooms" must be one of: ${validBlooms.join(', ')}`);
  }

  return {
    question: question.trim(),
    options: options.map((o: string) => o.trim()) as [string, string, string, string],
    correctIndex: correctIndex as 0 | 1 | 2 | 3,
    blooms: blooms as BloomsLevel,
    explanation: (typeof explanation === 'string' ? explanation : '').trim(),
  };
}

// ---- Video Quiz generation (Edpuzzle-style) ----------------

const VIDEO_LABELS = ['A', 'B', 'C', 'D', 'E', 'F'];

/**
 * Analyzes a YouTube video with Gemini and returns timestamped quiz questions
 * (a mix of multiple-choice and short-answer). Uses a direct REST call because
 * the video `fileData` part is the reliable path for YouTube URLs.
 */
export async function generateVideoQuestions(
  youtubeUrl: string,
  count = 5,
): Promise<VideoQuestion[]> {
  const apiKey = import.meta.env.VITE_GEMINI_API_KEY;
  if (!apiKey) throw new Error('VITE_GEMINI_API_KEY is not set in .env');
  const model = import.meta.env.VITE_GEMINI_MODEL || 'gemini-2.5-flash';

  const prompt =
    `Watch this video and create ${count} quiz questions spread evenly across its ` +
    `timeline for a live classroom. Mix multiple-choice and a few short open-ended ` +
    `questions. Respond ONLY with a JSON array; each item strictly:\n` +
    `{ "timestamp": <integer seconds into the video>, "kind": "mc" | "short", ` +
    `"question": string, "options": [4 strings] (mc only; omit for short), ` +
    `"correctIndex": 0-3 (mc only), ` +
    `"blooms": "Remember"|"Understand"|"Apply"|"Analyze"|"Evaluate"|"Create", ` +
    `"explanation": string }.\n` +
    `Order by timestamp ascending. Keep options under 80 chars. No "All/None of the above".`;

  const res = await fetch(
    `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${apiKey}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contents: [{ parts: [{ fileData: { fileUri: youtubeUrl } }, { text: prompt }] }],
        generationConfig: { responseMimeType: 'application/json', temperature: 0.4, maxOutputTokens: 4096 },
      }),
    },
  );
  if (!res.ok) {
    throw new Error(`Gemini video analysis failed: ${res.status} ${await res.text()}`);
  }
  const data = await res.json();
  const rawText: string = data?.candidates?.[0]?.content?.parts?.[0]?.text ?? '';
  return parseVideoQuestions(rawText);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function parseVideoQuestions(rawText: string): VideoQuestion[] {
  let arr: any[];
  try {
    arr = JSON.parse(rawText.replace(/^```json?\n?|```$/g, '').trim());
  } catch {
    throw new Error('AI returned invalid JSON for the video questions.');
  }
  if (!Array.isArray(arr)) throw new Error('AI response was not a list of questions.');

  const validBlooms: BloomsLevel[] = ['Remember', 'Understand', 'Apply', 'Analyze', 'Evaluate', 'Create'];

  return arr
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .map((q: any): VideoQuestion | null => {
      const question = typeof q?.question === 'string' ? q.question.trim() : '';
      if (!question) return null;
      const kind: 'mc' | 'short' = q?.kind === 'short' ? 'short' : 'mc';
      const timestamp = Math.max(0, Math.round(Number(q?.timestamp) || 0));
      const blooms = validBlooms.includes(q?.blooms) ? (q.blooms as BloomsLevel) : null;

      let options: MCOption[] | undefined;
      if (kind === 'mc' && Array.isArray(q?.options)) {
        const correct = Number.isInteger(q?.correctIndex) ? q.correctIndex : 0;
        const built: MCOption[] = q.options.slice(0, 6).map((text: unknown, i: number): MCOption => ({
          id: `opt_${i}_${Math.random().toString(36).slice(2, 6)}`,
          text: (String(text ?? '').trim()) || `Option ${VIDEO_LABELS[i]}`,
          isCorrect: i === correct,
        }));
        if (built.length >= 2) options = built; // else falls back to short
      }

      return {
        id: `vq_${Math.random().toString(36).slice(2, 9)}`,
        timestamp,
        kind: options ? 'mc' : 'short',
        question,
        options,
        starValue: 1,
        blooms,
        explanation: typeof q?.explanation === 'string' ? q.explanation.trim() : '',
      };
    })
    .filter((q): q is VideoQuestion => q !== null)
    .sort((a, b) => a.timestamp - b.timestamp);
}
